/* eslint-disable max-len */
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { ReferencePrice } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferencePrice';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import {
  ADD_ESTIMATE_KNOWN_REFERENCE_RESPONSE,
  ADD_ESTIMATE_TIRE_RESPONSE,
  AUTOCOMPLETE_BUNDLES_SEARCH_RESPONSE,
  AUTOCOMPLETE_REFERENCES_SEARCH_RESPONSE,
  B2B_ESTIMATE_CHECKOUT_ORDER_RESPONSE,
  B2B_ESTIMATE_SENT_TO_CLIENT_HISTORY_RESPONSE,
  EASYBASKET_ADDED_TO_ESTIMATE_RESPONSE,
  ESTIMATE_CREATED_RESPONSE,
  ESTIMATE_DMS_ORDER_NUMBERS_RESPONSE,
  ESTIMATE_UNAVAILABLE_RESPONSE,
  GET_B2B_ESTIMATE_REFERENCES_DISCOUNT_REMOVED,
  GET_B2B_ESTIMATE_REFERENCES_DISCOUNTS_RESPONSE,
  GET_B2B_ESTIMATE_REFERENCES_PRICE_RESPONSE,
  GET_B2B_ESTIMATE_REFERENCES_STOCKS_RESPONSE,
  GET_ESTIMATE_BY_ID_RESPONSE,
  GET_ESTIMATE_HISTORY_RESPONSE,
  GET_ESTIMATE_ID_TO_DELETE_RESPONSE,
  GET_ESTIMATE_ID_TO_HIDE_RESPONSE,
  GET_ESTIMATE_ID_TO_SHOW_RESPONSE,
  GET_ESTIMATE_SETTINGS_RESPONSE,
  SEND_ESTIMATE_TO_DMS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';

import {
  AddItemByReferenceNumber,
  VehicleDetail,
} from '@1po/1po-bff-fe-spec/generated/estimate/request/AddItemByReferenceNumber';
import { ItemType } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/ItemType';
import { LaborTime as CatalogLaborTime } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/LaborTime';
import { Reference as CatalogReference } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/Reference';
import { SearchType } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/SearchType';
import { Tire as CatalogTire } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/Tire';
import { BundleOrRecyclingField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateBundleOrRecycling';
import { ClientField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateClient';
import { LaborTimeField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateLaborTime';
import { OperationField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateOperation';
import { ReferenceField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateReference';
import { EstimateSettingsFilter, SettingsField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateSettings';
import { VehicleField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateVehicle';
import { AutocompleteSearchBundlesResponse } from '@1po/1po-bff-fe-spec/generated/estimate/response/AutocompleteSearchBundlesResponse';
import { AutocompleteSearchReferencesResponse } from '@1po/1po-bff-fe-spec/generated/estimate/response/AutocompleteSearchReferencesResponse';
import { EstimateDmsOrderNumbers } from '@1po/1po-bff-fe-spec/generated/estimate/response/EstimateDmsOrderNumbers';
import { EstimateUnavailableResponse } from '@1po/1po-bff-fe-spec/generated/estimate/response/EstimateUnavailableResponse';
import { GetEstimate } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { GetEstimateHistory } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimateHistory';
import { Settings } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetSettings';
import { MyStoreBundle } from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { MyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { EstimateCheckoutItem } from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutB2BEstimate';
import {
  OrderDeliveryType,
  OrderMKTPDeliveryMode,
} from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from 'app/AppStore';
import { ADD_VEHICLE_REFERENCES_FROM_ESTIMATE } from 'domains/basket/Basket.types';
import { NON_MKTP, ReferencePriceType } from 'domains/references';
import {
  ERROR,
  FOUND,
  getData,
  hasData,
  LOADING,
  mergeObjectInMap,
  mergeStocksInSearchDataMap,
  NO_DATA,
  NOT_FOUND,
  SEARCH_STATUS,
  SearchData,
} from 'utils';
import { isSingleUnitOrderRestrictedFamily } from 'utils/rights';
import {
  ADD_B2B_ESTIMATE_TO_CLIENT_HISTORY,
  ADD_BUNDLES_FROM_AUTOCOMPLETE,
  ADD_BUNDLES_FROM_MY_STORE,
  ADD_CATALOG_LABOR_TIME,
  ADD_CATALOG_LABOR_TIME_BY_CODE,
  ADD_CATALOG_REFERENCE,
  ADD_CATALOG_REFERENCE_BY_REFERENCE_NUMBER,
  ADD_CATALOG_TIRE,
  ADD_CATALOG_TIRE_BY_REFERENCE_NUMBER,
  ADD_CUSTOM_BUNDLE_OR_RECYCLING,
  ADD_CUSTOM_LABOR_TIME,
  ADD_CUSTOM_OPERATION_REFERENCE,
  ADD_CUSTOM_OPERATIONLABOR_TIME,
  ADD_CUSTOM_REFERENCE,
  ADD_CUSTOM_TIRE,
  ADD_EASYBASKET_OPERATION_TO_ESTIMATE,
  ADD_OPERATION,
  ADD_OTHER_ITEM,
  ADD_REFERENCE_FROM_AUTOCOMPLETE,
  ADD_REFERENCE_NUMBER,
  ADD_VEHICLE,
  BundleSubsectionSearch,
  CANCEL_ESTIMATE_DELETION,
  CHECKOUT_B2B_ESTIMATE,
  CREATE_ESTIMATE_FROM_DMS,
  DELETE_ESTIMATE,
  EasyBasketEstimateCreationStatus,
  ESTIMATE_NAMESPACE,
  EstimateLocal,
  EstimateState,
  EstimateType,
  FILL_FROM_MAINTENANCE_PLAN,
  GET_ESTIMATE_BY_ID,
  GET_HISTORY,
  GET_SETTINGS,
  ReferenceDiscountMargins,
  ReferenceSubsectionSearch,
  REMOVE_ALL_BUNDLES_OR_RECYCLING,
  REMOVE_ALL_LABOR_TIMES,
  REMOVE_ALL_OTHER_ITEMS,
  REMOVE_ALL_REFERENCES,
  REMOVE_ALL_TIRES,
  REMOVE_BUNDLE_OR_RECYCLING,
  REMOVE_KNOWN_REFERENCE_CONFIRMATION,
  REMOVE_LABOR_TIME,
  REMOVE_OPERATION_LABOR_TIME,
  REMOVE_OPERATION_REFERENCE,
  REMOVE_OTHER_ITEM,
  REMOVE_REFERENCE,
  REMOVE_TIRE,
  RequestReference,
  SEND_AUTOCOMPLETE_BUNDLES_SEARCH,
  SEND_AUTOCOMPLETE_REFERENCES_SEARCH,
  SEND_DMS_ORDER_NUMBER_REQUEST,
  SEND_ESTIMATE_TO_DMS,
  UPDATE_BUNDLE_OR_RECYCLING,
  UPDATE_CLIENT,
  UPDATE_COUNTERPARTIES,
  UPDATE_LABOR_TIME,
  UPDATE_OBSERVATIONS,
  UPDATE_OPERATION,
  UPDATE_OPERATION_LABOR_TIME,
  UPDATE_OPERATION_REFERENCE,
  UPDATE_REFERENCE,
  UPDATE_SETTINGS,
  UPDATE_VEHICLE,
} from './Estimate.types';

const getEstimate = (estimates: Map<string, EstimateLocal | NO_DATA>, estimateId?: string) => {
  if (!estimateId) {
    return undefined;
  }
  return getData(estimates.get(estimateId));
};

/**
 * Validate whether incoming buyer tresor code and seller tresor codes align with current client contact
 * Warn: client contact may be empty, in this case we should receive informations based on R1 profile
 *
 * @param estimate
 * @param incomingBuyerTresorCode
 * @param incomingSellerTresorCode
 */
const isInvalidB2BEstimateChange = (
  estimate: EstimateLocal,
  incomingBuyerTresorCode: string,
  incomingSellerTresorCode: string,
) => {
  const clientContact = estimate.clientContact;
  return (
    estimate.estimateType !== EstimateType.B2B ||
    (clientContact.tresorCode !== undefined && clientContact.tresorCode !== incomingBuyerTresorCode) ||
    (clientContact.sellerTresorCode !== undefined && clientContact.sellerTresorCode !== incomingSellerTresorCode)
  );
};
const getEstimateOperations = (estimate?: EstimateLocal) => estimate?.operationSection.operations ?? [];
const getEstimateReferences = (estimate?: EstimateLocal) => estimate?.referenceSubsection.references ?? [];
const getEstimateLaborTimes = (estimate?: EstimateLocal) => estimate?.laborTimeSubsection.laborTimes ?? [];
const getEstimateBundlesOrRecycling = (estimate?: EstimateLocal) => estimate?.bundleOrRecyclingSubsection.items ?? [];

const getNewEstimateData = (vehicle?: VehicleDetail): EstimateLocal => {
  const newEstimateId = uuidv4();
  const estimateVehicle =
    vehicle !== undefined
      ? {
          catalogSource: vehicle.catalogSource,
          vrn: vehicle.vrn,
          vin: vehicle.vin,
          vehicleKey: vehicle.vehicleKey,
          imageUrl: vehicle.imageUrl,
          vehicleBrand: vehicle.vehicleBrand,
          country: vehicle.country ?? '',
          model: vehicle.model ?? '',
          manufacturingDate: vehicle.manufacturingDate ?? '',
          mileage: '',
          parkNumber: '',
          administrationNumber: '',
          iamVehicle: vehicle?.iamVehicle,
          dataHubVehicle: vehicle?.dataHubVehicle,
          dialogAddToEstimateWasShown: true,
        }
      : undefined;
  return {
    ...initEstimate,
    estimateId: newEstimateId,
    vehicle: estimateVehicle,
  };
};

export const initEstimate: EstimateLocal = {
  totalPrice: {
    priceVatExcluded: '0',
    vat: '0',
    priceVatIncluded: '0',
    discount: '0',
  },
  estimateType: EstimateType.B2B, // might be changed in case that user should use b2b
  observations: undefined,
  dmsOrderNumber: undefined,
  vehicle: undefined,
  referenceSubsection: {
    totalPriceVatExcluded: '0',
    totalValidItems: 0,
    vat: '0',
    references: [],
  },
  laborTimeSubsection: {
    totalPriceVatExcluded: '0',
    vat: '0',
    totalValidItems: 0,
    laborTimes: [],
  },
  bundleOrRecyclingSubsection: {
    totalPriceVatExcluded: '0',
    vat: '0',
    totalValidItems: 0,
    items: [],
  },
  operationSection: {
    referencesTotalPriceVatExcluded: '0',
    referencesVat: '0',
    laborTimesTotalPriceVatExcluded: '0',
    laborTimesVat: '0',
    operations: [],
    totalValidItems: 0,
  },
  totalMargin: '0',
  totalMarginPercentage: '0',
  referenceDiscountMarginMap: new Map<string, ReferenceDiscountMargins>(),
  estimateId: '',
  creationDate: '',
  clientContact: {
    name: '',
    email: '',
    phoneNumber: '',
  },
  isHidden: false,
  dmsExportTime: '',
  estimateOrderItemDetails: {
    orderMark: '',
    mktpDeliveryMode: 'STANDARD',
    referenceMarks: new Map<string, string>(),
    isUrgentDelivery: new Map<string, boolean>(),
  },
  estimateOrderStatus: undefined,
  prices: new Map<string, SearchData<ReferencePriceType>>(),
  stockInfo: new Map<string, SearchData<Map<string, ReferenceStock>>>(),
  referenceDiscounts: new Map<string, ReferenceDiscount>(),
};

const initialBundleSearch: BundleSubsectionSearch = {
  fulltextAutocomplete: undefined,
  lastAutocompleteKeyCode: undefined,
  lastAutocompleteKeyDescription: undefined,
  fulltextSearchResult: undefined,
};

const initialReferenceSearch: ReferenceSubsectionSearch = {
  fulltextAutocomplete: undefined,
  lastAutocompleteKeyCode: undefined,
  lastAutocompleteKeyDescription: undefined,
  fulltextSearchResult: undefined,
};
const initialState = (): EstimateState => {
  const newEstimateData = getNewEstimateData();

  return {
    currentEstimateId: newEstimateData.estimateId,
    estimates: new Map<string, EstimateLocal>([[newEstimateData.estimateId, newEstimateData]]),
    nonApplicableReferencesConfirmations: [],
    history: {
      estimates: [],
      cursor: undefined,
      hasMore: true,
      searchStatus: undefined,
    },
    settings: undefined,
    estimateLogo: 'motrio',
    bundleOrRecyclingSubsectionSearch: initialBundleSearch,
    freeReferenceSubsectionSearch: initialReferenceSearch,
    easyBasketEstimateCreationStatus: undefined,
    isOrderDialogOpen: false,
  };
};

// Saga actions
export const fetchEstimateByIdRequest = createAction<string>(GET_ESTIMATE_BY_ID);
export const fetchEstimateByIdResponse = createAction(GET_ESTIMATE_BY_ID_RESPONSE);
export const fetchDeleteEstimateByIdResponse = createAction(GET_ESTIMATE_ID_TO_DELETE_RESPONSE);
export const fetchHideEstimateByIdResponse = createAction(GET_ESTIMATE_ID_TO_HIDE_RESPONSE);
export const fetchShowEstimateByIdResponse = createAction(GET_ESTIMATE_ID_TO_SHOW_RESPONSE);
export const fetchDmsOrderNumbersResponse = createAction(ESTIMATE_DMS_ORDER_NUMBERS_RESPONSE);
export const addDmsOrderNumberRequest = createAction<{
  estimateId: string;
  dmsOrderNumber: string;
}>(SEND_DMS_ORDER_NUMBER_REQUEST);
export const fetchEstimateCreatedResponse = createAction(ESTIMATE_CREATED_RESPONSE);
export const fetchEstimateUnavailableResponse = createAction<EstimateUnavailableResponse>(
  ESTIMATE_UNAVAILABLE_RESPONSE,
);
export const fetchEasybasketAddedToEstimateResponse = createAction(EASYBASKET_ADDED_TO_ESTIMATE_RESPONSE);
export const fetchEstimateHistoryRequest = createAction(GET_HISTORY);
export const fetchEstimateHistoryResponse = createAction(GET_ESTIMATE_HISTORY_RESPONSE);
export const addKnowReferenceByRefNumberResponse = createAction(ADD_ESTIMATE_KNOWN_REFERENCE_RESPONSE);
export const addTireByRefNumberResponse = createAction(ADD_ESTIMATE_TIRE_RESPONSE);

export const sendEstimateToDMSResponse = createAction(SEND_ESTIMATE_TO_DMS_RESPONSE);
export const addCatalogLaborTime = createAction<{
  laborTime: CatalogLaborTime;
  testLaborTimes?: CatalogLaborTime[];
}>(ADD_CATALOG_LABOR_TIME);
export const addCatalogReference = createAction<{
  reference: RequestReference;
}>(ADD_CATALOG_REFERENCE);
export const addCatalogTire = createAction<{
  reference: CatalogTire;
}>(ADD_CATALOG_TIRE);
export const addReferenceNumber = createAction<{
  referenceNumber: string;
  itemId?: string;
}>(ADD_REFERENCE_NUMBER);
export const addCatalogReferenceByReferenceNumber = createAction<{
  referenceNumber: string;
  itemId?: string;
  itemType: ItemType;
  searchType: SearchType;
}>(ADD_CATALOG_REFERENCE_BY_REFERENCE_NUMBER);
export const addCatalogTireByReferenceNumber = createAction<{
  referenceNumber: string;
  itemId?: string;
  itemType: ItemType;
  searchType: SearchType;
}>(ADD_CATALOG_TIRE_BY_REFERENCE_NUMBER);

export const addBundleFromMyStore = createAction<{
  bundles: MyStoreBundle[];
  quantity?: number;
}>(ADD_BUNDLES_FROM_MY_STORE);
export const addBundleFromAutocomplete = createAction<{ bundle: MyStoreBundle; itemId: string }>(
  ADD_BUNDLES_FROM_AUTOCOMPLETE,
);
export const addReferenceFromAutocomplete = createAction<{ reference: MyStoreReference; itemId: string }>(
  ADD_REFERENCE_FROM_AUTOCOMPLETE,
);
export const addCatalogReferencesToBasket = createAction(ADD_VEHICLE_REFERENCES_FROM_ESTIMATE);
export const addCatalogLaborTimeByCode = createAction<string>(ADD_CATALOG_LABOR_TIME_BY_CODE);
export const addCustomReference = createAction(ADD_CUSTOM_REFERENCE);
export const addCustomOperationReference = createAction<{ operationId?: string }>(ADD_CUSTOM_OPERATION_REFERENCE);
export const addCustomOperationLaborTime = createAction<{ operationId?: string }>(ADD_CUSTOM_OPERATIONLABOR_TIME);
export const addOperation = createAction(ADD_OPERATION);
export const addEasyBasketOperationToEstimate = createAction(ADD_EASYBASKET_OPERATION_TO_ESTIMATE);
export const addCustomLaborTime = createAction(ADD_CUSTOM_LABOR_TIME);
export const addCustomBundleOrRecycling = createAction(ADD_CUSTOM_BUNDLE_OR_RECYCLING);
export const addCustomTire = createAction(ADD_CUSTOM_TIRE);
export const addOtherItem = createAction(ADD_OTHER_ITEM);

export const updateReference = createAction<{
  itemId: string;
  parentItemId?: string;
  newValue: string;
  field: ReferenceField;
}>(UPDATE_REFERENCE);
export const updateOperationReference = createAction<{
  itemId: string;
  parentItemId?: string;
  operationId: string;
  newValue: string;
  field: ReferenceField;
}>(UPDATE_OPERATION_REFERENCE);
export const updateLaborTime = createAction<{
  itemId: string;
  newValue: string;
  field: LaborTimeField;
}>(UPDATE_LABOR_TIME);
export const updateOperationLaborTime = createAction<{
  itemId: string;
  operationId: string;
  newValue: string;
  field: LaborTimeField;
}>(UPDATE_OPERATION_LABOR_TIME);
export const updateBundleOrRecycling = createAction<{
  itemId: string;
  newValue: string;
  field: BundleOrRecyclingField;
}>(UPDATE_BUNDLE_OR_RECYCLING);
export const updateOperation = createAction<{
  itemId: string;
  newValue: string;
  field: OperationField;
}>(UPDATE_OPERATION);

export const removeReference = createAction<{ itemId: string; parentItemId?: string }>(REMOVE_REFERENCE);
export const removeOperationReference = createAction<{ itemId: string; operationId: string }>(
  REMOVE_OPERATION_REFERENCE,
);
export const removeLaborTime = createAction<string>(REMOVE_LABOR_TIME);
export const removeOperationLaborTime = createAction<{ itemId: string; operationId: string }>(
  REMOVE_OPERATION_LABOR_TIME,
);
export const removeTire = createAction<string>(REMOVE_TIRE);
export const removeOtherItem = createAction<string>(REMOVE_OTHER_ITEM);
export const removeBundleOrRecycling = createAction<string>(REMOVE_BUNDLE_OR_RECYCLING);
export const removeOperation = createAction<string>(REMOVE_BUNDLE_OR_RECYCLING);

export const removeAllReferences = createAction(REMOVE_ALL_REFERENCES);
export const removeAllLaborTimes = createAction(REMOVE_ALL_LABOR_TIMES);
export const removeAllBundlesOrRecycling = createAction(REMOVE_ALL_BUNDLES_OR_RECYCLING);
export const removeAllTires = createAction(REMOVE_ALL_TIRES);
export const removeAllOtherItems = createAction(REMOVE_ALL_OTHER_ITEMS);
export const removeKnownReferenceValidation = createAction<string>(REMOVE_KNOWN_REFERENCE_CONFIRMATION);
export const deleteEstimateRequest = createAction<{
  estimateId: string;
}>(DELETE_ESTIMATE);
export const cancelEstimateDeletion = createAction<{
  estimateId: string;
}>(CANCEL_ESTIMATE_DELETION);
export const addVehicle = createAction(ADD_VEHICLE);
export const updateVehicle = createAction<{
  newValue: string;
  field: VehicleField;
}>(UPDATE_VEHICLE);
export const updateClient = createAction<{
  newValue: string;
  field: ClientField;
}>(UPDATE_CLIENT);
export const updateB2BCounterParties = createAction<{
  sellerTresorCode: string;
  buyerTresorCode: string;
}>(UPDATE_COUNTERPARTIES);
export const updateObservations = createAction<string>(UPDATE_OBSERVATIONS);
export const fetchSettingsRequest = createAction(GET_SETTINGS);
export const fetchSettingsResponse = createAction(GET_ESTIMATE_SETTINGS_RESPONSE);
export const updateSettings = createAction<{
  newValue: string;
  field: SettingsField;
  filters?: EstimateSettingsFilter;
}>(UPDATE_SETTINGS);
export const createEstimateFromDMSRequest = createAction<{
  orderNumber: string;
}>(CREATE_ESTIMATE_FROM_DMS);
export const sendEstimateToDMSRequest = createAction(SEND_ESTIMATE_TO_DMS);

export const fillEstimateFromMaintenancePlan = createAction<{
  vehicleDetail: VehicleDetail;
  laborTimes?: CatalogLaborTime[];
  references?: CatalogReference[];
}>(FILL_FROM_MAINTENANCE_PLAN);

export const sendAutocompleteBundlesRequest = createAction<{
  queryCode: string;
  queryDescription: string;
}>(SEND_AUTOCOMPLETE_BUNDLES_SEARCH);
export const sendAutocompleteReferencesRequest = createAction<{
  queryReference: string;
  queryDescription: string;
}>(SEND_AUTOCOMPLETE_REFERENCES_SEARCH);

export const sendB2BEstimateToClientHistoryRequest = createAction<{ estimateId: string }>(
  ADD_B2B_ESTIMATE_TO_CLIENT_HISTORY,
);
export const sendB2BEstimateToClientHistoryResponse = createAction(B2B_ESTIMATE_SENT_TO_CLIENT_HISTORY_RESPONSE);
export const checkoutB2BEstimateRequest = createAction<{
  estimateId: string;
  checkoutItems: EstimateCheckoutItem[];
  deliveryType: OrderDeliveryType;
  paymentClientCode: string;
  instruction?: string;
}>(CHECKOUT_B2B_ESTIMATE);
export const checkoutB2BEstimateResponse = createAction(B2B_ESTIMATE_CHECKOUT_ORDER_RESPONSE);
export const fetchB2BReferencesPriceResponseSaga = createAction(GET_B2B_ESTIMATE_REFERENCES_PRICE_RESPONSE);
export const fetchB2BReferencesStockResponseSaga = createAction(GET_B2B_ESTIMATE_REFERENCES_STOCKS_RESPONSE);
export const fetchB2BReferencesDiscountsResponseSaga = createAction(GET_B2B_ESTIMATE_REFERENCES_DISCOUNTS_RESPONSE);
export const b2BReferencesDiscountsRemovedSaga = createAction(GET_B2B_ESTIMATE_REFERENCES_DISCOUNT_REMOVED);
export const sendAutocompleteBundlesResponse = createAction(AUTOCOMPLETE_BUNDLES_SEARCH_RESPONSE);
export const sendAutocompleteReferencesResponse = createAction(AUTOCOMPLETE_REFERENCES_SEARCH_RESPONSE);

// Slice
const slice = createSlice({
  initialState,
  name: ESTIMATE_NAMESPACE,
  reducers: {
    setInitialState: initialState,
    setEstimateHistoryResponse: (state, action: PayloadAction<GetEstimateHistory>) => {
      const { estimates, cursor, hasMore } = action.payload;

      const estimateIds = new Set(state.history.estimates.map((estimate) => estimate.estimateId));

      const newEstimates = estimates
        .filter((e) => !estimateIds.has(e.estimateId))
        .map((e) => {
          return { ...e, isHidden: false };
        });

      state.history.cursor = cursor;
      state.history.searchStatus = FOUND;
      state.history.estimates = state.history.estimates.concat(newEstimates);
      state.history.hasMore = hasMore;
    },
    setHistoryEstimate: (state, action: PayloadAction<GetEstimate>) => {
      const estimate = action.payload.estimate;

      const historyIndex = state.history.estimates.findIndex(
        (historyEstimate) => historyEstimate.estimateId === estimate.estimateId,
      );
      if (historyIndex !== -1) {
        state.history.estimates[historyIndex] = {
          ...state.history.estimates[historyIndex],
          clientName: estimate.clientContact.name,
          clientRrfCode: estimate.clientContact.rrfCode,
          vehicle: estimate.vehicle,
        };
      }
    },
    setEstimateResponse: (state, action: PayloadAction<GetEstimate>) => {
      const estimate = action.payload.estimate;

      const currEstimate = getData(state.estimates.get(estimate.estimateId));
      if (state.currentEstimateId === estimate.estimateId.substring(estimate.estimateId.length - 36)) {
        state.estimates.delete(state.currentEstimateId);
        state.currentEstimateId = estimate.estimateId;
      }

      mergeObjectInMap(state.estimates, estimate.estimateId, {
        ...estimate,
        estimateType: estimate.estimateType === 'B2B' ? EstimateType.B2B : EstimateType.B2C,
        isHidden: false,
        dmsExportTime: estimate.dmsExportTime,
        referenceDiscountMarginMap: currEstimate?.referenceDiscountMarginMap ?? initEstimate.referenceDiscountMarginMap,
        estimateOrderItemDetails: currEstimate?.estimateOrderItemDetails ?? initEstimate.estimateOrderItemDetails,
        estimateOrderStatus: currEstimate?.estimateOrderStatus ?? initEstimate.estimateOrderStatus,
        prices: currEstimate?.prices ?? initEstimate.prices,
        stockInfo: currEstimate?.stockInfo ?? initEstimate.stockInfo,
        referenceDiscounts: currEstimate?.referenceDiscounts ?? initEstimate.referenceDiscounts,
      });
    },
    setB2BEstimateReferencesPriceData: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        referencesPrice: ReferencePrice[];
      }>,
    ) => {
      const { estimateId, referencesPrice, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }

      referencesPrice.forEach((price) => {
        const newPrice =
          !price.currency && !price.clientView && !price.garageView
            ? undefined
            : {
                currency: price.currency,
                clientView: price.clientView,
                garageView: price.garageView,
              };

        mergeObjectInMap(estimate.prices, price.reference, {
          searchStatus: newPrice ? FOUND : NOT_FOUND,
          data: newPrice,
        });
      });
    },
    setB2BEstimateReferencesStockData: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        referencesStocks: ReferenceStock[];
      }>,
    ) => {
      const { estimateId, referencesStocks, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }
      referencesStocks.forEach((newStock) => {
        const isEmpty = newStock.warehouses === undefined || newStock.warehouses.length === 0;
        const objToMerge = new Map<string, ReferenceStock>().set(newStock.sellerNumber ?? NON_MKTP, {
          reference: newStock.reference,
          confirmedQuantity: newStock.confirmedQuantity,
          maxConfirmedQuantity: newStock.maxConfirmedQuantity,
          warehouses: newStock.warehouses,
          primaryStockResponse: newStock.primaryStockResponse,
          sellerName: newStock.sellerName,
          sellerNumber: newStock.sellerNumber,
        });
        estimate.stockInfo ??= new Map();
        mergeStocksInSearchDataMap(estimate.stockInfo, newStock.reference, {
          searchStatus: isEmpty ? NOT_FOUND : FOUND,
          data: objToMerge,
        });
      });
    },
    setB2BEstimateReferencesDiscountsData: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        referenceDiscounts: ReferenceDiscount[];
      }>,
    ) => {
      const { estimateId, referenceDiscounts, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }
      referenceDiscounts.forEach((discount) => {
        mergeObjectInMap(estimate.referenceDiscounts, discount.referenceNumber, discount);
      });
    },
    removeB2BEstimateReferencesDiscounts: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        references: string[];
      }>,
    ) => {
      const { estimateId, references, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }
      references.forEach((ref) => {
        estimate.referenceDiscounts.delete(ref);
      });
    },
    setB2BEstimateReferencesPriceNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { estimateId, referenceNumbers, status, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }

      referenceNumbers.forEach((ref) => {
        mergeObjectInMap(estimate.prices, ref, {
          searchStatus: status,
        });
      });
    },
    setB2BEstimateReferencesStockNoDataStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        estimateId: string;
        buyerTresorCode: string;
        sellerTresorCode: string;
        referenceNumbers: string[];
        status: NO_DATA;
      }>,
    ) => {
      const { estimateId, referenceNumbers, status, buyerTresorCode, sellerTresorCode } = payload;
      const estimate = state.estimates.get(estimateId);
      if (!hasData(estimate) || isInvalidB2BEstimateChange(estimate, buyerTresorCode, sellerTresorCode)) {
        return;
      }
      referenceNumbers.forEach((ref) => {
        mergeObjectInMap(estimate.stockInfo, ref, {
          searchStatus: status,
        });
      });
    },
    resetSellerBuyerInfo: (state, action: PayloadAction<string>) => {
      const estimate = getData(state.estimates.get(action.payload));
      if (!estimate) {
        return;
      }

      mergeObjectInMap(state.estimates, action.payload, {
        ...estimate,
        clientContact: { name: '', phoneNumber: '', email: '' },
        prices: initEstimate.prices,
        stockInfo: initEstimate.stockInfo,
        referenceDiscounts: initEstimate.referenceDiscounts,
      });
    },
    setCurrentEstimateId: (state, action: PayloadAction<string>) => {
      state.currentEstimateId = action.payload;
    },
    setCurrentEstimateIdLoading: (state, action: PayloadAction<string>) => {
      state.estimates.set(action.payload, LOADING);
      state.currentEstimateId = action.payload;
    },
    setEstimateDataStatus: (state, action: PayloadAction<{ estimateId: string; status: NO_DATA }>) => {
      const { estimateId, status } = action.payload;
      state.estimates.set(estimateId, status);
    },
    setEstimateHistorySearchStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.history.searchStatus = payload;
      if (payload === NOT_FOUND || payload === ERROR) {
        state.history.hasMore = false;
      }
    },
    setEstimateHistorySearch: (state, { payload }: PayloadAction<string | undefined>) => {
      state.history.search = payload;
    },
    setDmsOrderNumbers: (state, { payload }: PayloadAction<EstimateDmsOrderNumbers>) => {
      state.selectDmsOrderNumber = {
        searchStatus: FOUND,
        data: payload,
      };
    },
    resetDmsOrderNumbers: (state) => {
      state.selectDmsOrderNumber = undefined;
    },
    resetEstimateHistory: (state) => {
      state.history.cursor = undefined;
      state.history.estimates = [];
      state.history.hasMore = false;
    },
    createNewEstimate: (state, action: PayloadAction<{ vehicle: VehicleDetail | undefined }>) => {
      const { vehicle } = action.payload;
      const newEstimateData = getNewEstimateData(vehicle);

      state.currentEstimateId = newEstimateData.estimateId;
      state.estimates.set(newEstimateData.estimateId, newEstimateData);
      state.nonApplicableReferencesConfirmations = [];
    },
    addReferenceForValidation: (state, action: PayloadAction<AddItemByReferenceNumber>) => {
      state.nonApplicableReferencesConfirmations.push(action.payload.referenceNumber);
    },
    removeReferenceForValidation: (state, action: PayloadAction<string>) => {
      state.nonApplicableReferencesConfirmations = state.nonApplicableReferencesConfirmations.filter(
        (x) => x !== action.payload,
      );
    },
    setOrderEstimateStatus: (state, action: PayloadAction<{ estimateId: string; status: SEARCH_STATUS }>) => {
      const { estimateId, status } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      mergeObjectInMap(state.estimates, estimateId, {
        ...estimate,
        estimateOrderStatus: status,
      });
    },
    setEstimateOrderMark: (state, action: PayloadAction<{ estimateId: string; orderMark: string }>) => {
      const { estimateId, orderMark } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      mergeObjectInMap(state.estimates, estimateId, {
        ...estimate,
        estimateOrderItemDetails: {
          ...estimate.estimateOrderItemDetails,
          orderMark,
        },
      });
    },
    setEstimateOrderMktpDeliveryMode: (
      state,
      action: PayloadAction<{ estimateId: string; deliveryMode: OrderMKTPDeliveryMode }>,
    ) => {
      const { estimateId, deliveryMode } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      mergeObjectInMap(state.estimates, estimateId, {
        ...estimate,
        estimateOrderItemDetails: {
          ...estimate.estimateOrderItemDetails,
          mktpDeliveryMode: deliveryMode,
        },
      });
    },
    setEstimateOrderIsUrgent: (
      state,
      action: PayloadAction<{
        estimateId: string;
        referenceNumber: string;
        isUrgentDelivery: boolean;
      }>,
    ) => {
      const { estimateId, referenceNumber, isUrgentDelivery } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      estimate.estimateOrderItemDetails.isUrgentDelivery.set(referenceNumber, isUrgentDelivery);
    },
    setEstimateReferenceMark: (
      state,
      action: PayloadAction<{ estimateId: string; referenceNumber: string; referenceMark: string }>,
    ) => {
      const { estimateId, referenceNumber, referenceMark } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      estimate.estimateOrderItemDetails.referenceMarks.set(referenceNumber, referenceMark);
    },
    setEstimateHidden: (state, action: PayloadAction<{ estimateId: string; isHidden: boolean }>) => {
      const { estimateId, isHidden } = action.payload;
      const historyIndex = state.history.estimates.findIndex(
        (historyEstimate) => historyEstimate.estimateId === estimateId,
      );
      if (historyIndex !== -1) {
        state.history.estimates[historyIndex] = {
          ...state.history.estimates[historyIndex],
          isHidden,
        };
      }
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      mergeObjectInMap(state.estimates, estimate.estimateId, {
        ...estimate,
        isHidden,
      });
    },
    setEstimateRemoved: (state, action: PayloadAction<{ estimateId: string }>) => {
      const { estimateId } = action.payload;

      state.history.estimates = state.history.estimates.filter((estimate) => estimate.estimateId !== estimateId);
      state.estimates.delete(estimateId);
      if (state.currentEstimateId === estimateId) {
        state.nonApplicableReferencesConfirmations = [];
      }
      if (state.selectDmsOrderNumber?.data?.estimateId === estimateId) {
        state.selectDmsOrderNumber = undefined;
      }
      if (state.easyBasketEstimateCreationStatus?.estimateId === estimateId) {
        state.easyBasketEstimateCreationStatus = undefined;
      }
    },
    setEstimateLogo: (state, action: PayloadAction<string>) => {
      const logoId = action.payload;
      state.estimateLogo = state.estimateLogo === logoId ? undefined : logoId;
    },
    setOrderDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isOrderDialogOpen = action.payload;
    },
    setSettings: (state, action: PayloadAction<Settings | NO_DATA>) => {
      state.settings = action.payload;
    },
    resetEstimateSettingsForResubscription: (state) => {
      state.settings = undefined;
    },
    setDialogAddToEstimateWasShown: (state, action: PayloadAction<boolean>) => {
      const estimateId = state.currentEstimateId;
      if (!estimateId) {
        return;
      }
      const vehicle = getData(state.estimates.get(estimateId))?.vehicle;
      if (vehicle !== undefined) {
        vehicle.dialogAddToEstimateWasShown = action.payload;
      }
    },
    setEstimateReferenceDiscountMargins: (
      state,
      action: PayloadAction<{
        estimateId: string;
        referenceNumber: string;
        marginDiscount: number;
      }>,
    ) => {
      const { estimateId, referenceNumber, marginDiscount } = action.payload;
      const estimate = getData(state.estimates.get(estimateId));
      if (!estimate) {
        return;
      }
      estimate.referenceDiscountMarginMap.set(referenceNumber, { marginDiscount });
      state.estimates.set(estimateId, estimate);
    },
    setBundleFulltextAutocomplete: (state, action: PayloadAction<AutocompleteSearchBundlesResponse>) => {
      if (
        action.payload.searchQueryCode !== state.bundleOrRecyclingSubsectionSearch.lastAutocompleteKeyCode &&
        action.payload.searchQueryDesignation !== state.bundleOrRecyclingSubsectionSearch.lastAutocompleteKeyDescription
      ) {
        return;
      }
      state.bundleOrRecyclingSubsectionSearch = {
        ...state.bundleOrRecyclingSubsectionSearch,
        fulltextAutocomplete: action.payload.bundles,
      };
    },
    setBundleLastAutocompleteKey: (state, action: PayloadAction<{ searchCode: string; searchDescription: string }>) => {
      state.bundleOrRecyclingSubsectionSearch = {
        fulltextAutocomplete: undefined,
        fulltextSearchResult: undefined,
        lastAutocompleteKeyCode: action.payload.searchCode,
        lastAutocompleteKeyDescription: action.payload.searchDescription,
      };
    },
    setBundleFulltextAutocompleteNoData: (
      state,
      action: PayloadAction<{
        searchCode: string;
        searchDesignation: string;
        status: NO_DATA;
      }>,
    ) => {
      state.bundleOrRecyclingSubsectionSearch = {
        fulltextAutocomplete: undefined,
        fulltextSearchResult: undefined,
        lastAutocompleteKeyCode: action.payload.searchCode,
        lastAutocompleteKeyDescription: action.payload.searchDesignation,
      };
    },

    setReferenceFulltextAutocomplete: (state, action: PayloadAction<AutocompleteSearchReferencesResponse>) => {
      const { searchQueryReference, searchQueryDesignation, references } = action.payload;
      const { lastAutocompleteKeyCode, lastAutocompleteKeyDescription } = state.freeReferenceSubsectionSearch;
      const serverSentKeys = searchQueryReference !== undefined || searchQueryDesignation !== undefined;
      if (serverSentKeys) {
        const refMatches = searchQueryReference === lastAutocompleteKeyCode;
        const descMatches = searchQueryDesignation === lastAutocompleteKeyDescription;
        if (!refMatches && !descMatches) {
          return;
        }
      }
      state.freeReferenceSubsectionSearch = {
        ...state.freeReferenceSubsectionSearch,
        fulltextAutocomplete: references,
      };
    },
    setReferenceLastAutocompleteKey: (
      state,
      action: PayloadAction<{ searchCode: string; searchDescription: string }>,
    ) => {
      state.freeReferenceSubsectionSearch = {
        fulltextAutocomplete: undefined,
        fulltextSearchResult: undefined,
        lastAutocompleteKeyCode: action.payload.searchCode,
        lastAutocompleteKeyDescription: action.payload.searchDescription,
      };
    },
    setReferenceFulltextAutocompleteNoData: (
      state,
      action: PayloadAction<{
        searchCode: string;
        searchDesignation: string;
        status: NO_DATA;
      }>,
    ) => {
      state.freeReferenceSubsectionSearch = {
        fulltextAutocomplete: undefined,
        fulltextSearchResult: undefined,
        lastAutocompleteKeyCode: action.payload.searchCode,
        lastAutocompleteKeyDescription: action.payload.searchDesignation,
      };
    },
    setEasyBasketEstimateCreationStatus: (
      state,
      action: PayloadAction<EasyBasketEstimateCreationStatus | undefined>,
    ) => {
      state.easyBasketEstimateCreationStatus = action.payload;
    },
  },
});

// Actions
export const {
  setInitialState,
  setEstimateResponse,
  setEstimateHistoryResponse,
  setHistoryEstimate,
  createNewEstimate,
  setEstimateHistorySearchStatus,
  setEstimateHistorySearch,
  resetEstimateHistory,
  setCurrentEstimateId,
  resetSellerBuyerInfo,
  setEstimateDataStatus,
  setEstimateLogo,
  setSettings,
  resetEstimateSettingsForResubscription,
  removeReferenceForValidation,
  setEstimateHidden,
  setEstimateRemoved,
  addReferenceForValidation,
  setOrderEstimateStatus,
  setEstimateOrderMktpDeliveryMode,
  setEstimateReferenceMark,
  setEstimateOrderMark,
  setEstimateOrderIsUrgent,
  setDmsOrderNumbers,
  resetDmsOrderNumbers,
  setEstimateReferenceDiscountMargins,
  setBundleLastAutocompleteKey,
  setBundleFulltextAutocomplete,
  setReferenceFulltextAutocomplete,
  setReferenceLastAutocompleteKey,
  setBundleFulltextAutocompleteNoData,
  setB2BEstimateReferencesPriceData,
  setB2BEstimateReferencesStockData,
  setB2BEstimateReferencesDiscountsData,
  removeB2BEstimateReferencesDiscounts,
  setB2BEstimateReferencesStockNoDataStatus,
  setB2BEstimateReferencesPriceNoDataStatus,
  setReferenceFulltextAutocompleteNoData,
  setEasyBasketEstimateCreationStatus,
  setOrderDialogOpen,
} = slice.actions;

// Getters/Selectors
export const getCurrentEstimateId = createSelector(
  (state: RootState) => state.estimate.currentEstimateId,
  (currentEstimateId) => currentEstimateId,
);

export const getIsOrderDialogOpen = createSelector(
  (state: RootState) => state.estimate.isOrderDialogOpen,
  (isOrderDialogOpen) => isOrderDialogOpen,
);

export const getAllEstimates = createSelector(
  (state: RootState) => state.estimate.estimates,
  (estimates) => estimates,
);

export const getEstimateById = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    if (!estimateId) {
      return undefined;
    }
    return estimates.get(estimateId);
  },
);

export const getEstimateSequenceNumber = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.sequenceNumber,
);

export const getCurrentEstimate = createSelector(
  getCurrentEstimateId,
  (state: RootState) => state.estimate.estimates,
  (currentId, estimates) => getEstimate(estimates, currentId),
);

export const getEstimateVehicle = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.vehicle,
);

export const getEstimateVehicleKey = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.vehicle?.vehicleKey,
);

export const getEstimateVehicleDialogAddToEstimateWasShown = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.vehicle?.dialogAddToEstimateWasShown,
);

export const getCurrentEstimateVehicle = createSelector(getCurrentEstimate, (estimate) => estimate?.vehicle);

export const getEstimateClient = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.clientContact ?? initEstimate.clientContact,
);

export const getReferences = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateReferences(getEstimate(estimates, estimateId)),
);

export const getOperationReferences = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, { estimateId, operationId }: { estimateId: string; operationId?: string }) => {
    return {
      estimateId,
      operationId,
    };
  },
  (estimates, { estimateId, operationId }) =>
    getEstimateOperations(getEstimate(estimates, estimateId)).find((op) => op.itemId === operationId)
      ?.referenceSubsection.references || [],
);

export const getOperationLaborTimes = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, { estimateId, operationId }: { estimateId: string; operationId?: string }) => {
    return {
      estimateId,
      operationId,
    };
  },
  (estimates, { estimateId, operationId }) =>
    getEstimateOperations(getEstimate(estimates, estimateId)).find((op) => op.itemId === operationId)
      ?.laborTimeSubsection.laborTimes || [],
);

export const getReferencesWithValidPrices = createSelector(
  (state: RootState, estimateId: string) => getReferences(state, estimateId),
  (references) => references.filter((row) => row.unitPrice && row.vatPercentage),
);

export const getReferencesLength = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateReferences(getEstimate(estimates, estimateId)).length,
);

export const getPendingReferenceValidations = createSelector(
  (state: RootState) => state.estimate.nonApplicableReferencesConfirmations,
  (validations) => validations,
);

export const getCatalogReferences = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateReferences(getEstimate(estimates, estimateId)).filter((ref) => !ref.isCustom),
);

const hasTractionBatteryReference = (references: Array<{ commercialFamily?: string | null }>): boolean =>
  references.some((reference) => isSingleUnitOrderRestrictedFamily(reference.commercialFamily ?? undefined));

export const getHasTractionBatteryInReferences = createSelector(
  (state: RootState, estimateId: string) => getCatalogReferences(state, estimateId),
  (references) => hasTractionBatteryReference(references),
);

export const getHasTractionBatteryInEstimate = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    if (!estimate) {
      return false;
    }

    if (hasTractionBatteryReference(getEstimateReferences(estimate).filter((reference) => !reference.isCustom))) {
      return true;
    }

    return getEstimateOperations(estimate).some((operation) =>
      hasTractionBatteryReference(operation.referenceSubsection.references.filter((reference) => !reference.isCustom)),
    );
  },
);

export const getCatalogReferencesLength = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) =>
    getEstimateReferences(getEstimate(estimates, estimateId)).filter((ref) => !ref.isCustom).length,
);

export const getLaborTimes = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateLaborTimes(getEstimate(estimates, estimateId)),
);

export const getEstimateCreatedBy = createSelector(
  (state: RootState) => state.estimate.history.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => estimates.find((e) => e.estimateId === estimateId),
);

export const getLaborTimesWithValidPrices = createSelector(
  (state: RootState, estimateId: string) => getLaborTimes(state, estimateId),
  (laborTimes) => laborTimes.filter((row) => row.pricePerHour && row.vatPercentage && row.numberOfHours),
);
export const getLaborTimesLength = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateLaborTimes(getEstimate(estimates, estimateId)).length,
);

export const getBundlesOrRecyclingWithValidPrices = createSelector(
  (state: RootState, estimateId: string) => getBundlesOrRecycling(state, estimateId),
  (wts) => wts.filter((row) => row.unitPrice),
);

export const getBundlesOrRecyclingLength = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateBundlesOrRecycling(getEstimate(estimates, estimateId)).length,
);

export const getCatalogLaborTimes = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => getEstimateLaborTimes(getEstimate(estimates, estimateId)).filter((lt) => !lt.isCustom),
);

export const getCatalogOperationReferences = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) =>
    getEstimateOperations(getEstimate(estimates, estimateId)).flatMap((operation) =>
      operation.referenceSubsection.references.filter((ref) => !ref.isCustom),
    ),
);

export const getCatalogOperationReferencesLength = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) =>
    getEstimateOperations(getEstimate(estimates, estimateId)).reduce((total, operation) => {
      const catalogRefs = operation.referenceSubsection.references.filter((ref) => !ref.isCustom);
      return total + catalogRefs.length;
    }, 0),
);

export const getBundlesOrRecycling = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateBundlesOrRecycling(getEstimate(estimates, estimateId)),
);

export const getOperationsWithValidPrices = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const operations = getEstimateOperations(getEstimate(estimates, estimateId));
    return operations.map((op) => {
      return {
        ...op,
        referenceSubsection: {
          ...op.referenceSubsection,
          references: op.referenceSubsection.references.filter((ref) => ref.unitPrice && ref.vatPercentage),
        },
        laborTimeSubsection: {
          ...op.laborTimeSubsection,
          laborTimes: op.laborTimeSubsection.laborTimes.filter(
            (lt) => lt.pricePerHour && lt.vatPercentage && lt.numberOfHours,
          ),
        },
      };
    });
  },
);

export const getOperationsWithInvalidPrices = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimateOperations(getEstimate(estimates, estimateId)),
);

export const getRowTablesCount = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return (
      getEstimateLaborTimes(estimate).length +
      getEstimateReferences(estimate).length +
      getEstimateBundlesOrRecycling(estimate).length +
      getEstimateOperations(estimate).reduce((total, operation) => {
        return (
          total + operation.referenceSubsection.references.length + operation.laborTimeSubsection.laborTimes.length
        );
      }, 0)
    );
  },
);

export const getValidRowTablesCount = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    if (!estimate) {
      return 0;
    }
    return (
      (estimate.referenceSubsection.totalValidItems ?? 0) +
      (estimate.laborTimeSubsection.totalValidItems ?? 0) +
      (estimate.bundleOrRecyclingSubsection.totalValidItems ?? 0) +
      (estimate.operationSection.totalValidItems ?? 0)
    );
  },
);

export const getEstimateBadgeCount = (state: RootState, estimateId: string | undefined): number =>
  getValidRowTablesCount(state, estimateId);

export const getRealEstimateReferencesCount = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    if (!estimate) {
      return 0;
    }
    const realReferences = estimate.referenceSubsection.references.filter((ref) => !ref.isCustom);
    return realReferences.length;
  },
);

export const getEstimateTotalPrice = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.totalPrice ?? initEstimate.totalPrice,
);

export const getEstimateObservations = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.observations,
);

export const getEstimateReferenceSectionPriceVatExcl = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.referenceSubsection.totalPriceVatExcluded,
);

export const getEstimateOperationReferenceSectionPriceVatExcl = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const operationSection = getEstimate(estimates, estimateId)?.operationSection;
    if (!operationSection?.operations || operationSection?.operations.length === 0) {
      return '0';
    }
    return operationSection.referencesTotalPriceVatExcluded;
  },
);

export const getEstimateReferenceSectionVat = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.referenceSubsection.vat,
);

export const getEstimateOperationReferencesVat = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const operationSection = getEstimate(estimates, estimateId)?.operationSection;
    if (!operationSection?.operations || operationSection.operations.length === 0) {
      return '0';
    }
    return operationSection.referencesVat;
  },
);

export const getEstimateBundleOrRecyclingSectionPriceVatExcl = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.bundleOrRecyclingSubsection.totalPriceVatExcluded,
);

export const getEstimateBundleOrRecyclingSectionVat = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.bundleOrRecyclingSubsection.vat,
);

export const getTotalMargin = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.totalMargin,
);

export const getTotalMarginPercentage = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.totalMarginPercentage,
);

export const getEstimateLaborTimeSectionPriceVatExcl = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.laborTimeSubsection.totalPriceVatExcluded,
);

export const getEstimateOperationLaborTimesSectionPriceVatExcl = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const operationSection = getEstimate(estimates, estimateId)?.operationSection;
    if (!operationSection?.operations || operationSection.operations.length === 0) {
      return '0';
    }
    return operationSection.laborTimesTotalPriceVatExcluded;
  },
);

export const getEstimateLogo = createSelector(
  (state: RootState) => state.estimate.estimateLogo,
  (estimateLogo) => estimateLogo,
);

export const getEstimateLaborTimeSectionVat = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => getEstimate(estimates, estimateId)?.laborTimeSubsection.vat,
);
export const getEstimateOperationLaborTimesSectionVat = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId: string) => estimateId,
  (estimates, estimateId) => {
    const operationSection = getEstimate(estimates, estimateId)?.operationSection;
    if (!operationSection?.operations || operationSection.operations.length === 0) {
      return '0';
    }
    return operationSection.laborTimesVat;
  },
);

export const getEstimateHistory = createSelector(
  (state: RootState) => state.estimate.history.estimates,
  (historyEstimates) => historyEstimates.filter((e) => !e.isHidden),
);

export const getEstimateHistoryCursor = createSelector(
  (state: RootState) => state.estimate.history.cursor,
  (cursor) => cursor,
);

export const getEstimateHistoryHasMore = createSelector(
  (state: RootState) => state.estimate.history.hasMore,
  (hasMore) => hasMore,
);

export const getEstimateHistorySearch = createSelector(
  (state: RootState) => state.estimate.history.search,
  (search) => search,
);

export const getEstimateHistorySearchStatus = createSelector(
  (state: RootState) => state.estimate.history.searchStatus,
  (searchStatus) => searchStatus,
);

export const getEstimateSettings = createSelector(
  (state: RootState) => state.estimate.settings,
  (settings) => settings,
);

export const getEstimateReferenceDiscountSumMargin = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    if (!estimate) {
      return undefined;
    }

    const margins = Array.from(estimate.referenceDiscountMarginMap.values());
    return margins.reduce((acc, curr) => {
      return acc + curr.marginDiscount;
    }, 0);
  },
);

export const getEstimateB2BPrices = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return estimate?.prices ?? new Map<string, SearchData<ReferencePriceType>>();
  },
);

export const getEstimateB2BReferencePrice = createSelector(
  (state: RootState) => state.estimate.estimates,
  (
    _state: RootState,
    params: {
      estimateId: string;
      referenceNumber: string;
    },
  ) => params,
  (estimates, params) => {
    const estimate = getEstimate(estimates, params.estimateId);
    const prices = estimate?.prices ?? new Map<string, SearchData<ReferencePriceType>>();
    return prices.get(params.referenceNumber);
  },
);

export const getEstimateB2BStocks = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return estimate?.stockInfo ?? new Map<string, SearchData<Map<string, ReferenceStock>> | undefined>();
  },
);
export const getEstimateB2BReferenceStock = createSelector(
  (state: RootState) => state.estimate.estimates,
  (
    _state: RootState,
    params: {
      estimateId: string;
      referenceNumber: string;
    },
  ) => params,
  (estimates, params) => {
    const estimate = getEstimate(estimates, params.estimateId);
    const stocks = estimate?.stockInfo ?? new Map<string, SearchData<Map<string, ReferenceStock>> | undefined>();
    return stocks.get(params.referenceNumber);
  },
);

export const getEstimateB2BDiscounts = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return estimate?.referenceDiscounts ?? new Map<string, ReferenceDiscount>();
  },
);

export const getEstimateB2BReferenceDiscount = createSelector(
  (state: RootState) => state.estimate.estimates,
  (
    _state: RootState,
    params: {
      estimateId: string;
      referenceNumber: string;
    },
  ) => params,
  (estimates, params) => {
    const estimate = getEstimate(estimates, params.estimateId);
    const discounts = estimate?.referenceDiscounts ?? new Map<string, ReferenceDiscount>();
    return discounts.get(params.referenceNumber);
  },
);

export const getEstimateOrderItemDetails = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return estimate?.estimateOrderItemDetails;
  },
);

export const getEstimateOrderStatus = createSelector(
  (state: RootState) => state.estimate.estimates,
  (_state: RootState, estimateId?: string) => estimateId,
  (estimates, estimateId) => {
    const estimate = getEstimate(estimates, estimateId);
    return estimate?.estimateOrderStatus;
  },
);

export const getBundleFulltextAutocomplete = createSelector(
  (state: RootState) => state.estimate.bundleOrRecyclingSubsectionSearch,
  (query) => query?.fulltextAutocomplete,
);

export const getReferenceFulltextAutocomplete = createSelector(
  (state: RootState) => state.estimate.freeReferenceSubsectionSearch,
  (query) => query?.fulltextAutocomplete,
);

export const getFreeReferenceSubsectionSearch = createSelector(
  (state: RootState) => state.estimate.freeReferenceSubsectionSearch,
  (search) => search,
);

export const getDmsOrderNumbers = createSelector(
  (state: RootState) => state.estimate.selectDmsOrderNumber,
  (selectDmsOrderNumber) => selectDmsOrderNumber,
);

export const getEasyBasketEstimateCreationStatus = createSelector(
  (state: RootState) => state.estimate.easyBasketEstimateCreationStatus,
  (status) => status,
);

// Export reducer
export default slice.reducer;
